import { useEffect, useState } from "react";
import { trackButtonClick, trackTipDisplayMode } from "@/utils/analytics";
import { getStoredProductName } from "@/utils/product";
import { PRODUCT_LEXI_SOCIAL, minTips, totalTips } from "@/utils/vars";

export const SHOW_ALL_TIPS_KEY = "showAllTips";
// Set only when the person flips the Tip Display Mode switch themselves.
// The Lexi Social lock (TipsDisplayToggle) also saves "false", and that must
// not follow someone who upgrades: paid accounts see all 4 tips unless they
// chose the top 2 themselves.
export const SHOW_ALL_TIPS_CHOSEN_KEY = "showAllTipsChosen";

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore (private mode, blocked storage)
  }
};

// Signed in on a paid product (Lexi Pro, 7-Day Pass, ...): the same product
// check the tips pages use to decide Lexi Social.
const isPaidAccount = () => {
  const product = getStoredProductName();
  return (
    !!product && product.toLowerCase() !== PRODUCT_LEXI_SOCIAL.toLowerCase()
  );
};

/**
 * The saved tips display preference, with the defaults applied:
 * - nothing saved yet: all 4 tips;
 * - paid account with "top 2" it didn't choose (left by the Lexi Social
 *   lock before upgrading): all 4 tips.
 * Saves the default so every reader of "showAllTips" agrees.
 * @param {boolean} isPaid - is the signed-in account on a paid product
 * @returns {boolean} show all 4 tips
 */
export const resolveShowAllTips = (isPaid = isPaidAccount()) => {
  const saved = readStorage(SHOW_ALL_TIPS_KEY);
  const chosen = readStorage(SHOW_ALL_TIPS_CHOSEN_KEY) === "true";

  if (saved === null || (saved !== "true" && isPaid && !chosen)) {
    writeStorage(SHOW_ALL_TIPS_KEY, "true");
    return true;
  }
  return saved === "true";
};

/**
 * Custom hook to manage tips display preferences
 * @returns {Object} - Object containing showAllTips state and toggle function
 */
export const useTipsDisplay = () => {
  const [showAllTips, setShowAllTips] = useState(true);

  // Initialize from localStorage on mount
  useEffect(() => {
    setShowAllTips(resolveShowAllTips());
  }, []);

  // Set up event listeners for changes from other components
  useEffect(() => {
    const handleTipsDisplayChange = () => {
      setShowAllTips(readStorage(SHOW_ALL_TIPS_KEY) === "true");
    };
    const handleStorage = (e) => {
      if (e.key === SHOW_ALL_TIPS_KEY) {
        handleTipsDisplayChange();
      }
    };
    // Upgrading (or signing in to a paid account) applies the paid default
    const handleProductChange = () => {
      const before = readStorage(SHOW_ALL_TIPS_KEY);
      setShowAllTips(resolveShowAllTips());
      if (before !== readStorage(SHOW_ALL_TIPS_KEY)) {
        window.dispatchEvent(new Event("tipsDisplayChanged"));
      }
    };

    window.addEventListener("tipsDisplayChanged", handleTipsDisplayChange);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("lexi:product-updated", handleProductChange);

    return () => {
      window.removeEventListener("tipsDisplayChanged", handleTipsDisplayChange);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("lexi:product-updated", handleProductChange);
    };
  }, []);

  // Function to toggle tips display preference
  const toggleTipsDisplay = (checked) => {
    setShowAllTips(checked);
    writeStorage(SHOW_ALL_TIPS_KEY, String(checked));
    writeStorage(SHOW_ALL_TIPS_CHOSEN_KEY, "true");

    const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
    const tipsCount = checked ? totalTips : minTips;

    // Track this interaction with analytics
    trackButtonClick("tips_display_toggle", {
      is_showing_all_tips: checked,
      tips_count: tipsCount,
      page_path: pagePath,
    });
    trackTipDisplayMode({
      tipsCount,
      isShowingAllTips: checked,
      pagePath,
    });

    // Dispatch an event so other components can react to this change
    window.dispatchEvent(new Event("tipsDisplayChanged"));
  };

  // Get current number of tips to display
  const tipsCount = showAllTips ? totalTips : minTips;

  return {
    showAllTips,
    toggleTipsDisplay,
    tipsCount,
  };
};
